import React, { createContext, useState } from "react";
import {
  useCourse,
  useEnrollments,
  useUserAssignments
} from "../hooks/learningCenter/myCourses";
import { useDispatch, useSelector } from "react-redux";
import ChooseVersionModalDialog from "../components/Courses/ChooseVersionModalDialog";
import EnrollOnInactiveModalDialog from "../components/Courses/EnrollOnInactiveModalDialog";
import { isIlt } from "../utils/course_utils";
import { assignmentStatuses, courseTypes, specialErrors } from "../constants";
import ChangeEnrollmentModal from "../components/InPersonAssignment/ChangeEnrollmentModal";
import { buildCoursePlayerRoute } from "../routes/LearningCenterRoutes";
import { push } from "connected-react-router";
import useLoadingState from "../hooks/useLoadingState";
import { UPDATE_USER_ASSIGNMENT } from "../redux/actions/userAssignmentsListActions";
import { useSnackbarNotifications } from "../hooks/snackbar";
import AddToCalendarModal from "../components/ShortCourses/AddToCalendarModal";
import userAssignmentsService from "../services/userAssignmentsService";
import { useTranslation } from "react-i18next";

const UserAssignmentContext = createContext();

const initialChangeEnrollmentModalData = {
  newVersion: null
};

const initialChooseVersionModalData = {
  versions: null,
  userAssignment: null
};

const UserAssignmentProvider = ({ children }) => {
  const [chooseVersionModalData, setChooseVersionModalData] = useState(
    initialChooseVersionModalData
  );
  const [changeEnrollmentModalData, setChangeEnrollmentModalData] = useState(
    initialChangeEnrollmentModalData
  );
  const [calendarModalOpen, setCalendarModalOpen] = useState({
    assignment: null,
    open: false
  });
  const [calendarModalData, setCalendarModalData] = useState(null);
  const [openCoursesInNewTab, setOpenCoursesInNewTab] = useState(false);
  const [needsRefetch, setNeedsRefetch] = useState(false);
  const [enrollOnInactiveModalData, setEnrollOnInactiveModalData] = useState(
    null
  );

  const dispatch = useDispatch();
  const { fetchCourse } = useCourse();
  const { enrollIntoCourse } = useEnrollments();
  const isUpdatingUserAssignment = useLoadingState(UPDATE_USER_ASSIGNMENT);
  const {
    createUserAssignment,
    deleteUserAssignment,
    updateUserAssignment
  } = useUserAssignments();
  const { displayErrorMessage } = useSnackbarNotifications();
  const { t } = useTranslation();
  const enrollmentError = (error, isMultiversioned, isIlt) => {
    console.error(error);
    if (error === specialErrors.enrollOnInactive) {
      setEnrollOnInactiveModalData({
        isMultiversioned: isMultiversioned,
        isIlt: isIlt
      });
    } else {
      displayErrorMessage(t("error.generic.title"));
    }
  };

  const user = useSelector(({ userState }) => userState.user);

  const isEnrolled = trainingObject => {
    // Returns true if the training object is a parent and has a user assignment
    // with an enrolled training object or if the training object is a child
    // and it is the user assignment's enrolled training object
    return (
      (!trainingObject.parentId &&
        !!trainingObject.userAssignment?.enrolledTrainingObject) ||
      (trainingObject.parentId &&
        trainingObject.userAssignment?.enrolledTrainingObject?.id ===
          trainingObject.id)
    );
  };

  const isStarted = userAssignment =>
    userAssignment &&
    (userAssignment.status == assignmentStatuses.inProgress ||
      userAssignment.status == assignmentStatuses.pending ||
      userAssignment.status == assignmentStatuses.complete ||
      (userAssignment.status == assignmentStatuses.failed &&
        userAssignment.courseType == courseTypes.ilt));

  const onResumeAssignment = userAssignment => {
    const coursePlayerRoute = buildCoursePlayerRoute(
      userAssignment.assignedTrainingObjectId
    );
    fetchCourse(userAssignment.enrolledTrainingObjectId);

    if (openCoursesInNewTab) {
      window.open(coursePlayerRoute, "_blank");
    } else {
      dispatch(push(coursePlayerRoute));
    }
  };

  const onStartCourse = trainingObject => {
    const userAssignment = trainingObject.userAssignment;
    const hasBrokenEnrollment =
      userAssignment.status === assignmentStatuses.needsUnenroll;
    if (hasBrokenEnrollment) {
      if (trainingObject.versions?.length === 0) {
        updateUserAssignment(userAssignment.id, null);
      }
      setChooseVersionModalData({
        versions: trainingObject.versions,
        userAssignment
      });
    } else if (userAssignment.enrolledTrainingObjectId) {
      if (
        !trainingObject.parentId ||
        userAssignment.enrolledTrainingObjectId == trainingObject.id
      ) {
        // Resume the course if the user clicks play on a parent training object
        // with an enrolled user assignment or if the training object is a child
        // and it is the user assignment's enrolled training object
        onResumeAssignment(userAssignment);
      } else {
        // The user is enrolling into a different child training object
        onEnrollInNewVersion(trainingObject, userAssignment);
      }
    } else {
      if (trainingObject.parentId) {
        // The user clicked enroll on a child training object
        updateUserAssignment(
          userAssignment.id,
          trainingObject.releaseId
        ).catch(error => enrollmentError(error, null, isIlt(trainingObject)));
      } else if (trainingObject.versions?.length === 1) {
        // The user clicked play on a parent training object with only one child,
        // so we directly enroll the user into that child
        const releaseId = trainingObject.versions[0].releaseId;
        return enrollIntoCourse(
          userAssignment.id,
          releaseId,
          true,
          openCoursesInNewTab
        ).catch(error =>
          enrollmentError(
            error,
            trainingObject.versions?.length > 1,
            isIlt(trainingObject)
          )
        );
      } else {
        setChooseVersionModalData({
          versions: trainingObject.versions,
          userAssignment
        });
      }
    }
  };

  const openChooseVersionModal = userAssignment => {
    setChooseVersionModalData({
      versions: userAssignment.versions,
      userAssignment
    });
  };

  const onStartAssignment = userAssignment => {
    let navigation;
    const hasBrokenEnrollment =
      userAssignment.status === assignmentStatuses.needsUnenroll;
    if (hasBrokenEnrollment) {
      if (userAssignment.versions?.length === 0) {
        updateUserAssignment(userAssignment.id, null);
      }
      openChooseVersionModal(userAssignment);
    } else if (
      !isIlt(userAssignment) &&
      (userAssignment.versions?.length === 1 ||
        userAssignment.enrolledTrainingObject?.type == "version")
    ) {
      // Immediately enroll user into course if course is online and only one version exists
      const version = userAssignment.enrolledTrainingObject
        ? userAssignment.enrolledTrainingObject.releaseId
        : userAssignment.versions[0].releaseId;

      const navigationSource = userAssignment.versions
        ? userAssignment.versions[0]
        : userAssignment.enrolledTrainingObject;

      navigation = navigationSource.showOverview ? "overview" : "first";

      enrollIntoCourse(
        userAssignment.id,
        version,
        navigation,
        openCoursesInNewTab
      ).catch(error =>
        enrollmentError(
          error,
          userAssignment.versions?.length > 1,
          isIlt(userAssignment)
        )
      );
    } else if (
      isIlt(userAssignment) &&
      (userAssignment.enrollmentStatus == "self" ||
        userAssignment.enrollmentStatus == "admin")
    ) {
      navigation = userAssignment.enrolledTrainingObject?.showOverview
        ? "overview"
        : "first";
      enrollIntoCourse(
        userAssignment.id,
        userAssignment.enrolledCtoaId,
        navigation,
        openCoursesInNewTab
      ).catch(error =>
        enrollmentError(error, userAssignment.versions?.length > 1, true)
      );
    } else {
      openChooseVersionModal(userAssignment);
    }
  };

  const onCreateUserAssignment = (
    trainingObject,
    forceEnrollment,
    releaseId = null,
    navigation = null
  ) => {
    return createUserAssignment(
      trainingObject.id,
      forceEnrollment,
      releaseId,
      navigation
    ).catch(error =>
      enrollmentError(
        error,
        !!trainingObject.parentId ? null : trainingObject.versions?.length > 1,
        isIlt(trainingObject)
      )
    );
  };

  const onDeleteUserAssignment = userAssignment => {
    return deleteUserAssignment(userAssignment.id);
  };

  const onUnenrollFromCourse = userAssignment => {
    return updateUserAssignment(userAssignment.id, null).then(() => {
      setChooseVersionModalData(initialChooseVersionModalData);
    });
  };

  const onChooseVersion = (version, userAssignment, enrollAndJoin) => {
    const hasBrokenEnrollment =
      userAssignment.status === assignmentStatuses.needsUnenroll;
    if (hasBrokenEnrollment) {
      return updateUserAssignment(
        userAssignment.id,
        version.releaseId,
        enrollAndJoin
      );
    } else {
      return enrollIntoCourse(
        userAssignment.id,
        version.releaseId,
        enrollAndJoin,
        openCoursesInNewTab
      ).catch(error => enrollmentError(error, true, isIlt(userAssignment)));
    }
  };

  const handleChangeEnrollment = isWithinTimeFromNow => {
    return updateUserAssignment(
      chooseVersionModalData.userAssignment.id,
      changeEnrollmentModalData.newVersion.releaseId,
      isWithinTimeFromNow
    ).then(() => {
      setChangeEnrollmentModalData(initialChangeEnrollmentModalData);
      setChooseVersionModalData(initialChooseVersionModalData);
    });
  };

  // Opens the Change Enrollment modal when the user attemps to enroll
  // directly into a different session
  const onEnrollInNewVersion = (newVersion, userAssignment) => {
    setChooseVersionModalData({
      userAssignment
    });
    setChangeEnrollmentModalData({
      newVersion
    });
  };

  const onAddToCalendarClick = (
    assignment,
    courseSection = null,
    shortCourse = null
  ) => {
    const calendarData = userAssignmentsService.getAssignmentCalendars(
      assignment,
      courseSection,
      shortCourse
    );
    setCalendarModalData(calendarData);
  };

  const onCloseCalendarModalClick = () => {
    setCalendarModalData(null);
    setCalendarModalOpen({
      assignment: null,
      open: false
    });
  };

  const contextValue = {
    onChangeEnrollment: openChooseVersionModal,
    onStartCourse,
    onStartAssignment,
    onDeleteUserAssignment,
    onCreateUserAssignment,
    onResumeAssignment,
    isEnrolled,
    isStarted,
    enrollIntoCourse,
    isUpdatingUserAssignment,
    onAddToCalendarClick,
    calendarModalOpen,
    setCalendarModalOpen,
    setOpenCoursesInNewTab,
    needsRefetch,
    setNeedsRefetch
  };

  return (
    <UserAssignmentContext.Provider value={contextValue}>
      {children}
      <ChooseVersionModalDialog
        user={user}
        courseVersions={chooseVersionModalData.versions}
        assignment={chooseVersionModalData.userAssignment}
        showDialog={
          !!chooseVersionModalData.userAssignment &&
          !!chooseVersionModalData.versions
        }
        close={() => setChooseVersionModalData(initialChooseVersionModalData)}
        enrollIntoCourse={(version, userAssignment, enrollAndJoin) =>
          onChooseVersion(version, userAssignment, enrollAndJoin)
        }
        setChangeEnrollmentModal={(versions, newVersion) => {
          setChangeEnrollmentModalData({
            newVersion
          });
        }}
        unenrollFromVersion={onUnenrollFromCourse}
      />
      <ChangeEnrollmentModal
        open={!!changeEnrollmentModalData.newVersion}
        onClose={() =>
          setChangeEnrollmentModalData(initialChangeEnrollmentModalData)
        }
        currentAssignment={
          chooseVersionModalData.userAssignment?.enrolledTrainingObject
        }
        newAssignment={changeEnrollmentModalData.newVersion}
        userAssignmentId={chooseVersionModalData.userAssignment?.id}
        isAdminEnrolled={
          chooseVersionModalData.userAssignment?.enrollmentStatus?.toLowerCase() ===
          "admin"
        }
        handleChangeEnrollment={handleChangeEnrollment}
        isUpdating={isUpdatingUserAssignment}
      />
      <AddToCalendarModal
        open={!!calendarModalData}
        onClose={onCloseCalendarModalClick}
        calendars={calendarModalData}
      />
      <EnrollOnInactiveModalDialog
        isIlt={enrollOnInactiveModalData?.isIlt}
        isMultiversioned={enrollOnInactiveModalData?.isMultiversioned}
        open={!!enrollOnInactiveModalData}
        onClose={() => {
          setEnrollOnInactiveModalData(null);
          setNeedsRefetch(true);
        }}
      />
    </UserAssignmentContext.Provider>
  );
};

export default UserAssignmentContext;

export { UserAssignmentProvider };
